import { THEME_OPTION } from 'enums';
import { createAppSelector } from 'store/hooks';

export const isTokenAddedToInterceptorSelector = createAppSelector(
  [(state) => state.setting.isTokenAddedToInterceptor],
  (isTokenAddedToInterceptor) => {
    return !!isTokenAddedToInterceptor;
  }
);

export const themeSelector = createAppSelector(
  [(state) => state.setting.themeMode as THEME_OPTION],
  (theme) => {
    return theme;
  }
);

export const selectedGoalSelector = createAppSelector(
  [
    (state) => state.user.details,
    (state) => state.user.isLogTermGoalUpdating,
    (state) => state.routineSuggestions.selectedGoals
  ],
  (userInfo, isLogTermGoalUpdating, selectedGoals) => {
    return { userInfo, isLogTermGoalUpdating, selectedGoals };
  }
);

export const routineExploreHabitPacksSelector = createAppSelector(
  [
    (state) => state.habitPack.onboardingHabitPacks,
    (state) => state.habitPack.areHabitPacksLoading
  ],
  (onboardingHabitPacks, areHabitPacksLoading) => ({
    onboardingHabitPacks,
    areHabitPacksLoading
  })
);

export const routineHabitPackDetailsSelector = createAppSelector(
  [
    (state) => state.habitPack.installingPack,
    (state) => state.setting.platform,
    (state) => state.habitPack.isSendingInstalledHabitPackId
  ],
  (installingPack, platform, isSendingInstalledHabitPackId) => {
    return {
      installingPack,
      platform,
      isSendingInstalledHabitPackId
    };
  }
);

export const tabBodySelector = createAppSelector(
  [
    (state) => state.setting.mode,
    (state) => state.setting.tabs,
    (state) => state.setting.activeTabIndex,
    (state) => state.setting.platform,
    (state) => state.setting.customRoutine,
    (state) => state.setting.flags,
    (state) => state.user.isUserAdmin,
    (state) => state.habitPack.packEditorTabMode,
    (state) => state.modal.confirmAction.showConfirmModal
  ],
  (
    mode,
    tabs,
    activeTabIndex,
    platform,
    customRoutine,
    flags,
    isUserAdmin,
    packEditorTabMode
  ) => {
    return {
      mode,
      tabs,
      activeTabIndex,
      platform,
      customRoutine,
      flags,
      isUserAdmin,
      packEditorTabMode
    };
  }
);

export const morningActivitiesSelector = createAppSelector(
  [
    (state) => state.setting.currentSettings.morning_activities,
    (state) => state.filter,
    (state) => state.setting.isSettingLoading
  ],
  (morning_activities, filter, isSettingLoading) => {
    return { morning_activities, filter, isSettingLoading };
  }
);

export const eveningActivitiesSelector = createAppSelector(
  [
    (state) => state.setting.currentSettings.evening_activities,
    (state) => state.filter,
    (state) => state.setting.isSettingLoading
  ],
  (evening_activities, filter, isSettingLoading) => {
    return { evening_activities, filter, isSettingLoading };
  }
);

export const breakActivitiesSelector = createAppSelector(
  [
    (state) => state.setting.currentSettings.break_activities,
    (state) => state.filter,
    (state) => state.setting.isSettingLoading
  ],
  (break_activities, filter, isSettingLoading) => {
    return { break_activities, filter, isSettingLoading };
  }
);

export const standaloneActivitiesSelector = createAppSelector(
  [
    (state) => state.setting.currentSettings.standalone_activities,
    (state) => state.filter,
    (state) => state.setting.isSettingLoading
  ],
  (standalone_activities, filter, isSettingLoading) => {
    return { standalone_activities, filter, isSettingLoading };
  }
);

export const errorPageSelector = createAppSelector(
  [(state) => ({ ...state.setting.error })],
  (error) => error
);

export const errorMessageSelector = createAppSelector(
  [(state) => state.setting.error.message],
  (errorMessage) => {
    return errorMessage;
  }
);

export const habitPackAndLibraryActivitiesAutocompleteInputSelector =
  createAppSelector(
    [
      (state) => state.setting.activeTabIndex,
      (state) => state.setting.tabs,
      (state) => state.setting.customRoutine,
      (state) => state.setting.currentSettings,
      (state) => state.routineSuggestions.routineSuggestionPayload.routines,
      (state) => state.setting.activityLibrary,
      (state) => state.modal.choice.choices,
      (state) => state.habitPack.marketplaceApprovedPacks,
      (state) => state.setting.mode,
      (state) => state.routineSuggestions.rawRoutineSuggestions,
      (state) => state.setting.platform,
      (state) => state.setting.recentHabitIcons
    ],
    (
      activeTabIndex,
      tabs,
      customRoutine,
      currentSettings,
      routineSuggestionsSelectedRoutines,
      activityLibrary,
      choices,
      marketplaceApprovedPacks,
      mode,
      rawRoutineSuggestions,
      platform,
      recentHabitIcons
    ) => {
      return {
        activeTabIndex,
        tabs,
        customRoutine,
        currentSettings,
        routineSuggestionsSelectedRoutines,
        activityLibrary,
        choices,
        marketplaceApprovedPacks,
        mode,
        rawRoutineSuggestions,
        platform,
        recentHabitIcons
      };
    }
  );

export const timingSelector = createAppSelector(
  [
    (state) => state.setting.currentSettings.startup_time,
    (state) => state.setting.currentSettings.shutdown_time,
    (state) => state.setting.currentSettings.break_after_minutes,
    (state) =>
      state.setting.currentSettings
        .cutoff_time_for_non_high_priority_activities,
    (state) => state.setting.routineTimeValidation,
    (state) => state.setting.currentSettings.evening_activities,
    (state) => state.setting.highlightShutdownTime,
    (state) => state.setting.highlightCutoffTime,
    (state) => state.setting.isSettingLoading,
    (state) => state.user.focus_modes,
    (state) => state.setting.flags,
    (state) => state.setting.platform
  ],
  (
    startup_time,
    shutdown_time,
    break_after_minutes,
    globalCutoffTime,
    routineTimeValidation,
    evening_activities,
    highlightShutdownTime,
    highlightCutoffTime,
    isSettingLoading,
    userFocusModes,
    flags,
    platform
  ) => {
    return {
      startup_time,
      shutdown_time,
      break_after_minutes,
      globalCutoffTime,
      routineTimeValidation,
      evening_activities,
      highlightShutdownTime,
      highlightCutoffTime,
      isSettingLoading,
      userFocusModes,
      flags,
      platform
    };
  }
);

export const platformSelector = createAppSelector(
  [(state) => state.setting.platform],
  (platform) => {
    return { platform };
  }
);

export const enableEditHabitsSelector = createAppSelector(
  [(state) => state.setting.platform, (state) => state.setting.flags],
  (platform, flags) => {
    return { platform, flags };
  }
);

export const timingSummarySelector = createAppSelector(
  [
    (state) => state.setting.currentSettings.startup_time,
    (state) => state.setting.currentSettings.shutdown_time,

    (state) =>
      state.setting.currentSettings
        .cutoff_time_for_non_high_priority_activities,
    (state) => state.setting.currentSettings.morning_activities,
    (state) => state.setting.currentSettings.evening_activities
  ],
  (
    startup_time,
    shutdown_time,
    cutoff_time_for_non_high_priority_activities,
    morning_activities,
    evening_activities
  ) => ({
    startup_time,
    shutdown_time,
    cutoff_time_for_non_high_priority_activities,
    morning_activities,
    evening_activities
  })
);

export const timingSummaryNavigationSelector = createAppSelector(
  [
    (state) => state.setting.tabs,
    (state) => state.setting.platform,
    (state) => state.setting.customRoutine,
    (state) => state.setting.flags,
    (state) => state.user.isUserAdmin
  ],
  (tabs, platform, customRoutine, flags, isUserAdmin) => ({
    tabs,
    platform,
    customRoutine,
    flags,
    isUserAdmin
  })
);

export const addHabitButtonSelector = createAppSelector(
  [
    (state) => state.setting.currentSettings,
    (state) => state.setting.tabs,
    (state) => state.setting.activeTabIndex,
    (state) => state.setting.customRoutine,
    (state) => state.routineSuggestions.routineSuggestionPayload.routines,
    (state) => state.setting.platform
  ],
  (
    currentSettings,
    tabs,
    activeTabIndex,
    customRoutine,
    routines,
    platform
  ) => ({
    currentSettings,
    tabs,
    activeTabIndex,
    customRoutine,
    routines,
    platform
  })
);

export const tabBodyHeaderSelector = createAppSelector(
  [
    (state) => state.setting.tabs,
    (state) => state.setting.activeTabIndex,
    (state) => state.setting.mode,
    (state) => state.setting.customRoutine,
    (state) => state.setting.platform
  ],
  (tabs, activeTabIndex, mode, customRoutine, platform) => {
    return {
      tabs,
      activeTabIndex,
      mode,
      customRoutine,
      platform
    };
  }
);

export const tabBodyHeaderWidgetsSelector = createAppSelector(
  [
    (state) => state.filter.isSettingToolbarShown,
    (state) => state.setting.activeTabIndex,
    (state) => state.setting.tabs,
    (state) => state.setting.customRoutine,
    (state) => state.setting.flags
  ],
  (isSettingToolbarShown, activeTabIndex, tabs, customRoutine, flags) => {
    return {
      isSettingToolbarShown,
      activeTabIndex,
      tabs,
      customRoutine,
      flags
    };
  }
);

export const newActivityButtonSelector = createAppSelector(
  [
    (state) => state.setting.activeTabIndex,
    (state) => state.setting.tabs,
    (state) => state.setting.currentSettings,
    (state) => state.setting.customRoutine,
    (state) => state.user.focus_modes
  ],
  (activeTabIndex, tabs, currentSettings, customRoutine, userFocusModes) => {
    return {
      activeTabIndex,
      tabs,
      currentSettings,
      customRoutine,
      userFocusModes
    };
  }
);

export const tabBodyRowDurationSelector = createAppSelector(
  [
    (state) => state.modal.moreOption.refreshDuration,
    (state) => state.setting.currentSettings.evening_activities,
    (state) =>
      state.setting.currentSettings
        .cutoff_time_for_non_high_priority_activities,
    (state) => state.setting.currentSettings.shutdown_time,
    (state) => state.user.focus_modes ?? []
  ],
  (
    refreshDuration,
    evening_activities,
    cutoff_time,
    shutdown_time,
    user_focus_modes
  ) => {
    return {
      refreshDuration,
      evening_activities,
      cutoff_time,
      shutdown_time,
      user_focus_modes
    };
  }
);

export const addNewActivityButtonSelector = createAppSelector(
  [
    (state) => state.setting.activeTabIndex,
    (state) => state.setting.tabs,
    (state) => state.setting.customRoutine,
    (state) => state.setting.currentSettings,
    (state) => state.routineSuggestions.routineSuggestionPayload.routines,
    (state) => state.setting.platform
  ],
  (
    activeTabIndex,
    tabs,
    customRoutine,
    currentSettings,
    routineSuggestionsSelectedRoutines,
    platform
  ) => {
    return {
      activeTabIndex,
      tabs,
      customRoutine,
      currentSettings,
      routineSuggestionsSelectedRoutines,
      platform
    };
  }
);

export const tabActionsSelector = createAppSelector(
  [
    (state) => state.setting.currentSettings,
    (state) => state.setting.previousSettings,
    (state) => state.setting.platform,
    (state) => state.setting.isUserSettingsModified,
    (state) => state.setting.flags,
    (state) => state.focusMode.currentViewedFocusTemplate,
    (state) => state.setting.validationErrors,
    (state) => state.setting.areSettingsValid,
    (state) => state.setting.areSettingsSaving,
    (state) => state.habitPack.isUserUpsertingPack,
    (state) => state.general.apiRateLimitedUntil,
    (state) => state.setting.isSettingLoaded,
    (state) => state.setting.isSettingLoading
  ],
  (
    currentSettings,
    previousSettings,
    platform,
    isUserSettingsModified,
    flags,
    currentViewedFocusTemplate,
    errors,
    areSettingsValid,
    areSettingsSaving,
    isUserUpsertingPack,
    isApiRateLimitedUntil,
    isSettingLoaded,
    isSettingLoading
  ) => {
    return {
      currentSettings,
      previousSettings,
      platform,
      isUserSettingsModified,
      flags,
      currentViewedFocusTemplate,
      errors,
      areSettingsValid,
      areSettingsSaving,
      isUserUpsertingPack,
      isApiRateLimitedUntil,
      isSettingLoaded,
      isSettingLoading
    };
  }
);

export const validationErrorsSelector = createAppSelector(
  [
    (state) => state.setting.currentSettings,
    (state) => state.setting.validationErrors,
    (state) => state.setting.mode,
    (state) => state.focusMode.currentViewedFocusTemplate
  ],
  (currentSettings, validationErrors, mode, currentViewedFocusTemplate) => ({
    currentSettings,
    validationErrors,
    mode,
    currentViewedFocusTemplate
  })
);

export const copyActivitySelector = createAppSelector(
  [
    (state) => state.modal.moreOption.activity,
    (state) => state.setting.moreOptions,
    (state) => state.setting.currentSettings
  ],
  (activity, moreOptions, currentSettings) => {
    return {
      activity,
      moreOptions,
      currentSettings
    };
  }
);

export const tabBodyRowNameSelector = createAppSelector(
  [(state) => state.setting.mode],
  (mode) => {
    return mode;
  }
);

export const moreOptionsApplicationsSelector = createAppSelector(
  [
    (state) => state.setting.suggestions,
    (state) => state.setting.platform,
    (state) => state.modal.moreOption.activity,
    (state) => state.modal.moreOption.isUseApplicationsChecked,
    (state) => state.modal.moreOption.isUseWebsiteChecked
  ],
  (
    suggestions,
    platform,
    activity,
    isUseApplicationsChecked,
    isUseWebsiteChecked
  ) => ({
    suggestions,
    platform,
    activity,
    isUseApplicationsChecked,
    isUseWebsiteChecked
  })
);

export const moreOptionsWebsitesSelector = createAppSelector(
  [
    (state) => state.modal.moreOption.activity,
    (state) => state.modal.moreOption.isUseWebsiteChecked
  ],
  (activity, isUseWebsiteChecked) => ({
    activity,
    isUseWebsiteChecked
  })
);

export const navMenuSelector = createAppSelector(
  [(state) => state.setting.currentSettings],
  (currentSettings) => currentSettings
);

export const routinePlayerSelector = createAppSelector(
  [
    (state) => state.setting.isSettingLoaded,
    (state) => state.setting.isDeviceRegistering,
    (state) => state.setting.showSkipActivityModal,
    (state) => state.setting.isSavingRoutineCompletedActivity,
    (state) => state.user.isCurrentActivityPropsUpdating,
    (state) => state.user.currentActivityProps.last_completed_sequence_at,
    (state) =>
      state.modal.routineActivitiesStats.showRoutineActivitiesStatsModal
  ],
  (
    isSettingLoaded,
    isDeviceRegistering,
    showSkipActivityModal,
    isSavingRoutineCompletedActivity,
    isCurrentActivityPropsUpdating,
    last_completed_sequence_at,
    showRoutineActivitiesStatsModal
  ) => {
    return {
      isSettingLoaded,
      isDeviceRegistering,
      showSkipActivityModal,
      isSavingRoutineCompletedActivity,
      isCurrentActivityPropsUpdating,
      last_completed_sequence_at,
      showRoutineActivitiesStatsModal
    };
  }
);

export const activityPlayerSelector = createAppSelector(
  [
    (state) => state.setting.currentPlayingActivity,
    (state) => state.setting.routinePlayerActivities,
    (state) => state.setting.isSavingRoutineCompletedActivity,
    (state) => state.setting.isFetchingNextRoutineActivity,
    (state) => state.setting.device,
    (state) => state.user.last_completed_activity_log,
    (state) => state.modal.note,
    (state) => state.modal.logQuantity
  ],
  (
    currentPlayingActivity,
    routinePlayerActivities,
    isSavingRoutineCompletedActivity,
    isFetchingNextRoutineActivity,
    device,
    last_completed_activity_log,
    noteModal,
    logQuantityModal
  ) => {
    return {
      currentPlayingActivity,
      routinePlayerActivities,
      isSavingRoutineCompletedActivity,
      isFetchingNextRoutineActivity,
      device,
      last_completed_activity_log,
      noteModal,
      logQuantityModal
    };
  }
);

export const routineActivitiesWrapperSelector = createAppSelector(
  [(state) => state.setting.isNewHabitAppended],
  (isNewHabitAppended) => {
    return isNewHabitAppended;
  }
);

export const surveyPageSelector = createAppSelector(
  [
    (state) => state.user.isSavingUserFeedback,
    (state) => state.user.showThankYouModal,
    (state) => state.setting.feedback
  ],
  (isSavingUserFeedback, showThankYouModal, feedback) => {
    return {
      isSavingUserFeedback,
      showThankYouModal,
      feedback
    };
  }
);
export const surveyQuestionsSelector = createAppSelector(
  [(state) => state.setting.feedback, (state) => state.setting.platform],
  (feedback, platform) => {
    return {
      feedback,
      platform
    };
  }
);

export const moreOptionsTimeSelector = createAppSelector(
  [
    (state) => state.setting.moreOptions.type,
    (state) => state.modal.moreOption.activity.priority
  ],
  (activityType, activityPriority) => {
    return {
      activityType,
      activityPriority
    };
  }
);

export const customRoutinesSelector = createAppSelector(
  [
    (state) => state.setting.currentSettings.custom_routines,
    (state) => state.setting.isSettingLoading
  ],
  (customRoutines, isSettingLoading) => {
    return {
      customRoutines,
      isSettingLoading
    };
  }
);

export const customRoutineSelector = createAppSelector(
  [(state) => state.setting.isNewCustomRoutineAppended],
  (isNewCustomRoutineAppended) => {
    return !!isNewCustomRoutineAppended;
  }
);

export const moreOptionsMicroBreakSelector = createAppSelector(
  [(state) => state.modal.moreOption.activity],
  (activity) => {
    return activity;
  }
);

export const moreOptionsAllowedAppsSelector = createAppSelector(
  [(state) => state.setting.moreOptions.type],
  (activityType) => {
    return { activityType };
  }
);

export const moreOptionsAllowedAppsFocusModeSelector = createAppSelector(
  [
    (state) => state.user.focus_modes,
    (state) => state.modal.moreOption.activity,
    (state) => state.modal.moreOption.isUseFocusModeChecked,
    (state) => state.user.isFetchingUserFocusModes
  ],
  (focus_modes, activity, isUseFocusModeChecked, isFetchingUserFocusModes) => {
    return {
      focus_modes,
      activity,
      isUseFocusModeChecked,
      isFetchingUserFocusModes
    };
  }
);

export const tabsSelector = createAppSelector(
  [
    (state) => state.setting.currentSettings,
    (state) => state.setting.previousSettings,
    (state) => state.setting.platform,
    (state) => state.setting.mode,
    (state) => state.user.isUserAdmin,
    (state) => state.setting.isTextEditorWithinCharacterLimit,
    (state) => state.setting.isSettingEditingFinished,
    (state) => state.setting.areSettingsSaving,
    (state) => state.habitPack.isUserUpsertingPack,
    (state) => state.focusMode.currentViewedFocusTemplate,
    (state) => state.setting.isShowMoreOptionsOpened,
    (state) => state.setting.routineTimeValidation,
    (state) => state.setting.flags,
    (state) => state.setting.areSettingsValid,
    (state) => state.setting.isSettingLoaded,
    (state) => state.setting.isSettingLoading
  ],
  (
    currentSettings,
    previousSettings,
    platform,
    mode,
    isUserAdmin,
    isTextEditorWithinCharacterLimit,
    isSettingEditingFinished,
    areSettingsSaving,
    isUserUpsertingPack,
    currentViewedFocusTemplate,
    isShowMoreOptionsOpened,
    routineTimeValidation,
    flags,
    areSettingsValid,
    isSettingLoaded,
    isSettingLoading
  ) => {
    return {
      currentSettings,
      previousSettings,
      platform,
      mode,
      isUserAdmin,
      isTextEditorWithinCharacterLimit,
      isSettingEditingFinished,
      areSettingsSaving,
      isUserUpsertingPack,
      currentViewedFocusTemplate,
      isShowMoreOptionsOpened,
      routineTimeValidation,
      flags,
      areSettingsValid,
      isSettingLoaded,
      isSettingLoading
    };
  }
);

export const settingGeneratorRevampSelector = createAppSelector(
  [
    (state) => state.setting.isUnlockHabitPackModalShown,
    (state) => state.setting.areSettingsSaving
  ],
  (isUnlockHabitPackModalShown, areSettingsSaving) => {
    return {
      isUnlockHabitPackModalShown,
      areSettingsSaving
    };
  }
);

export const languageDropDownSelector = createAppSelector(
  [(state) => state.setting.language],
  (language) => {
    return {
      language
    };
  }
);

export const draggableHeaderSelector = createAppSelector(
  [
    (state) => state.setting.currentSettings.morning_activities,
    (state) => state.setting.currentSettings.break_activities,
    (state) => state.setting.currentSettings.evening_activities,
    (state) => state.setting.currentSettings.standalone_activities,
    (state) => state.setting.activityLibrary,
    (state) => state.setting.mode
  ],
  (
    morning_activities,
    break_activities,
    evening_activities,
    standalone_activities,
    activityLibrary,
    mode
  ) => {
    return {
      morning_activities,
      break_activities,
      evening_activities,
      standalone_activities,
      activityLibrary,
      mode
    };
  }
);

export const desktopStatsUISelector = createAppSelector(
  [(state) => state.setting.statsPageActiveTabIndex],
  (statsPageActiveTabIndex) => {
    return statsPageActiveTabIndex;
  }
);

export const tabBodyRowWrapperSelector = createAppSelector(
  [
    (state) => state.setting.currentSettings,
    (state) => state.setting.activityLibrary
  ],
  (currentSettings, activityLibrary) => {
    return {
      currentSettings,
      activityLibrary
    };
  }
);

export const homePageSelector = createAppSelector(
  [
    (state) => state.setting.mode,
    (state) => state.setting.isSettingLoaded,
    (state) => state.setting.currentSettings
  ],
  (mode, isSettingLoaded, currentSettings) => {
    return {
      mode,
      isSettingLoaded,
      currentSettings
    };
  }
);

export const moreOptionsAdvancedSelector = createAppSelector(
  [
    (state) => state.setting.moreOptions.type,
    (state) => state.setting.mode,
    (state) => state.habitPack.packEditorTabMode,
    (state) => state.modal.moreOption.activity,
    (state) => state.modal.moreOption.isNewActivity,
    (state) => state.user.isUserAdmin
  ],
  (
    activityType,
    mode,
    packEditorTabMode,
    activity,
    isNewActivity,
    isUserAdmin
  ) => {
    return {
      activityType,
      mode,
      packEditorTabMode,
      activity,
      isNewActivity,
      isUserAdmin
    };
  }
);

export const moreOptionsActivityCutoffTimeSelector = createAppSelector(
  [
    (state) => state.modal.moreOption.activity.cutoff_time_for_doing_activity,
    (state) => state.modal.moreOption.activity.isLateCutoffEnabled,
    // Used only to seed a default when late cutoff is toggled on — not a constraint.
    (state) =>
      state.setting.currentSettings
        .cutoff_time_for_non_high_priority_activities,
    (state) => state.setting.currentSettings.shutdown_time
  ],
  (activityCutoffTime, isLateCutoffEnabled, globalCutoffTime, shutdownTime) => {
    return {
      activityCutoffTime,
      isLateCutoffEnabled,
      globalCutoffTime,
      shutdownTime
    };
  }
);

export const mobileApplicationsBreakSelector = createAppSelector(
  [
    (state) => state.setting.isUpdatingDeviceSettings,
    (state) => state.setting.localDeviceSettings,
    (state) => state.setting.platform
  ],
  (isUpdatingDeviceSettings, localDeviceSettings, platform) => {
    return {
      isUpdatingDeviceSettings,
      localDeviceSettings,
      platform
    };
  }
);

export const activeMarketTabSelector = createAppSelector(
  [(state) => state.setting.activeMarketTab],
  (activeMarketTab) => {
    return activeMarketTab;
  }
);

export const adminWidgetToolbarSelector = createAppSelector(
  [
    (state) => state.setting.tabs,
    (state) => state.setting.activeTabIndex,
    (state) => state.setting.currentSettings,
    (state) => state.focusMode.currentViewedFocusTemplate
  ],
  (tabs, activeTabIndex, currentSettings, currentViewedFocusTemplate) => {
    return {
      tabs,
      activeTabIndex,
      currentSettings,
      currentViewedFocusTemplate
    };
  }
);

export const languageSelector = createAppSelector(
  [(state) => state.setting.language],
  (language) => {
    return language;
  }
);

export const draggableContentSelector = createAppSelector(
  [
    (state) => state.setting.activeTabIndex,
    (state) => state.setting.tabs,
    (state) => state.setting.currentSettings.morning_activities,
    (state) => state.setting.currentSettings.break_activities,
    (state) => state.setting.currentSettings.evening_activities,
    (state) => state.setting.currentSettings.standalone_activities
  ],
  (
    activeTabIndex,
    tabs,
    morning_activities,
    break_activities,
    evening_activities,
    standalone_activities
  ) => {
    return {
      activeTabIndex,
      tabs,
      morning_activities,
      break_activities,
      evening_activities,
      standalone_activities
    };
  }
);

export const settingsGeneratorSelector = createAppSelector(
  [
    (state) => state.setting.currentSettings,
    (state) => state.setting.previousSettings,
    (state) => state.setting.platform,
    (state) => state.setting.mode,
    (state) => state.setting.isTextEditorWithinCharacterLimit,
    (state) => state.setting.isSettingEditingFinished,
    (state) => state.focusMode.currentViewedFocusTemplate,
    (state) => state.setting.routineTimeValidation,
    (state) => state.setting.flags,
    (state) => state.setting.areSettingsValid,
    (state) => state.habitPack.initialCurrentViewedPack,
    (state) => state.focusMode.initialCurrentViewedFocusTemplate,
    (state) => state.setting.isUserSettingsModified
  ],
  (
    currentSettings,
    previousSettings,
    platform,
    mode,
    isTextEditorWithinCharacterLimit,
    isSettingEditingFinished,
    currentViewedFocusTemplate,
    routineTimeValidation,
    flags,
    areSettingsValid,
    initialCurrentViewedPack,
    initialCurrentViewedFocusTemplate,
    isUserSettingsModified
  ) => {
    return {
      currentSettings,
      previousSettings,
      platform,
      mode,
      isTextEditorWithinCharacterLimit,
      isSettingEditingFinished,
      currentViewedFocusTemplate,
      routineTimeValidation,
      flags,
      areSettingsValid,
      initialCurrentViewedPack,
      initialCurrentViewedFocusTemplate,
      isUserSettingsModified
    };
  }
);

export const feedbackSelector = createAppSelector(
  [(state) => state.setting.feedback],
  (feedback) => {
    return feedback;
  }
);

export const weeklyStatisticsSelector = createAppSelector(
  [
    (state) => state.setting.isSettingLoaded,
    (state) => state.setting.currentSettings,
    (state) => state.user.isWeeklyStatsUpdating,
    (state) => state.user.weeklySummaryStats
  ],
  (
    isSettingLoaded,
    currentSettings,
    isWeeklyStatsUpdating,
    weeklySummaryStats
  ) => {
    return {
      isSettingLoaded,
      currentSettings,
      isWeeklyStatsUpdating,
      weeklySummaryStats
    };
  }
);

export const sideNavigationSelector = createAppSelector(
  [(state) => state.user.isUserAdmin, (state) => state.dayPlan.selectedDay],
  (isUserAdmin, selectedDay) => {
    return {
      isUserAdmin,
      selectedDay
    };
  }
);

export const navBarSelector = createAppSelector(
  [
    (state) => state.user.isUserAdmin,
    (state) => state.user.adminForTeams,
    (state) => state.team.teams,
    (state) => state.classManagement.classes,
    (state) => state.team.areTeamsLoading
  ],
  (isUserAdmin, adminForTeams, userTeams, teacherClasses, areTeamsLoading) => {
    return {
      isUserAdmin,
      adminForTeams,
      userTeams,
      teacherClasses,
      areTeamsLoading
    };
  }
);

export const sideNavAccountSelector = createAppSelector(
  [
    (state) => state.user.details?.name,
    (state) => state.user.details?.email,
    (state) => state.setting.themeMode,
    (state) => state.setting.language
  ],
  (userName, userEmail, themeMode, language) => {
    return { userName, userEmail, themeMode, language };
  }
);

export const filterPrioritySelector = createAppSelector(
  [(state) => state.filter.priority],
  (priority) => {
    return { priority };
  }
);

export const filterDaysOfWeekSelector = createAppSelector(
  [(state) => state.filter.daysOfWeek],
  (daysOfWeek) => {
    return { daysOfWeek };
  }
);

export const filterSearchWordSelector = createAppSelector(
  [(state) => state.filter.searchWord],
  (searchWord) => {
    return { searchWord };
  }
);

export const activityLibrarySelector = createAppSelector(
  [(state) => state.setting.activityLibrary],
  (activityLibrary) => {
    return { activityLibrary };
  }
);

export const routineTimeBoundariesSelector = createAppSelector(
  [
    (state) => state.setting.isSettingLoading,
    (state) => state.setting.activeTabIndex
  ],
  (isSettingLoading, activeTabIndex) => {
    return { isSettingLoading, activeTabIndex };
  }
);

export const habitEmojiPickerSelector = createAppSelector(
  [(state) => state.setting.language],
  (language) => {
    return { language };
  }
);

export const advancedBtnSelector = createAppSelector(
  [
    (state) => state.setting.mode,
    (state) => state.setting.activeTabIndex,
    (state) => state.habitPack.packEditorTabMode
  ],
  (mode, activeTabIndex, packEditorTabMode) => {
    return { mode, activeTabIndex, packEditorTabMode };
  }
);

export const tabBodyHeaderRevampSelector = createAppSelector(
  [
    (state) => state.filter.isSettingToolbarShown,
    (state) => state.setting.activeTabIndex,
    (state) => state.setting.tabs,
    (state) => state.filter.searchWord,
    (state) => state.habitPack.packEditorTabMode
  ],
  (
    isSettingToolbarShown,
    activeTabIndex,
    tabs,
    searchWord,
    packEditorTabMode
  ) => {
    return {
      isSettingToolbarShown,
      activeTabIndex,
      tabs,
      searchWord,
      packEditorTabMode
    };
  }
);

export const habitStatsPageSelector = createAppSelector(
  [
    (state) => state.setting.isSettingLoaded,
    (state) => state.setting.isSettingLoading
  ],
  (isSettingLoaded, isSettingLoading) => {
    return {
      isSettingLoaded,
      isSettingLoading
    };
  }
);

export const habitStatsRoutineActivitiesSelector = createAppSelector(
  [
    (state) => state.setting.currentSettings.morning_activities,
    (state) => state.setting.currentSettings.break_activities,
    (state) => state.setting.currentSettings.evening_activities,
    (state) => state.user.details.timezone,
    (state) => state.setting.currentSettings.created_at
  ],
  (
    morning_activities,
    break_activities,
    evening_activities,
    timezone,
    created_at
  ) => {
    return {
      morning_activities,
      break_activities,
      evening_activities,
      timezone,
      created_at
    };
  }
);

export const routineLevelSelector = createAppSelector(
  [(state) => state.user.areOnboardingStatsLoading],
  (areOnboardingStatsLoading) => {
    return { areOnboardingStatsLoading };
  }
);
